import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import useProject from "../../hooks/useProject";
import useBrowser from "../../hooks/useBrowser";
import usePage from "../../hooks/usePage";

import Loading from "../ui/Loading";
import EditorToolbar from "../editors/EditorToolbar";
import LocationItem from "./LocationItem";
import Select from "../elements/Select";
import Input from "../elements/Input";

import CallsModal from "../modals/CallsModal";
import ProjectLocationModal from "../modals/ProjectLocationModal";
import LocationListingsModal from "../modals/LocationListingsModal";

const ProjectLocations = ({ projectId, onRefresh }) => {
  const navigate = useNavigate();
  const { windowHeight } = useBrowser();
  const { showMessage } = usePage();
  const {
    project,
    projectLocations,
    getProjectLocations,
    getNextLocations,
    removeProjectLocations,
    getProjectBanners,
    projectBanners,
    getProjectReps,
    projectReps,
    getProjectTerritories,
    projectTerritories,
    locationCount,
  } = useProject(projectId);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [filter, setFilter] = useState(null);
  const [showCalls, setShowCalls] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showListings, setShowListings] = useState(false);
  const [selectedLocation, setSelectedlocation] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const recordsPerPage = 10;

  useEffect(() => {
    if (project) {
      setCurrentIndex(0);
      getData();
    }
  }, [project, filter]);

  const getData = async () => {
    await getProjectLocations(filter, { currentIndex: 0, recordsPerPage });
    await getProjectBanners();
    // await getProjectReps();
    await getProjectTerritories();
  };

  useEffect(() => {
    getNextLocations(filter, { currentIndex, recordsPerPage });
  }, [currentIndex]);

  const getNext = async () => {
    setCurrentIndex((current) => current + recordsPerPage);
  };

  const removeLocations = async () => {
    const results = await removeProjectLocations();
    await getData();
    onRefresh();
  };

  const handleFilterChange = (field, value) => {
    setCurrentIndex(0);
    setFilter((current) => {
      if (value !== "all") {
        return { ...current, [field]: value };
      }
      let newFilter = { ...current };
      delete newFilter[field];

      return newFilter;
    });
  };

  const handleShowCalls = () => {
    setShowCalls(true);
    handleMenuClose();
  };

  const handleShowLocation = () => {
    setShowLocation(true);
    handleMenuClose();
  };

  const handleShowListings = () => {
    setShowListings(true);
    handleMenuClose();
  };

  const handleCloseCalls = () => {
    setShowCalls(false);
    setSelectedlocation(null);
  };

  const handleCloseLocation = () => {
    setShowLocation(false);
    setSelectedlocation(null);
  };

  const handleCloseListings = () => {
    setShowListings(false);
    setSelectedlocation(null);
  };

  const handleMenuClick = (event, location) => {
    setSelectedlocation(location.locationId);

    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddLocations = () => {
    navigate("/locations", {
      state: {
        returnUrl: "/project",
        returnState: { projectId: projectId, clientId: projectId },
        projectId: projectId,
      },
    });
  };

  if (!project || !Array.isArray(projectLocations)) return <Loading />;

  return (
    <>
      <Box
        sx={{
          height: `${windowHeight - 170}px`,
        }}
      >
        <EditorToolbar
          onAdd={handleAddLocations}
          onDelete={removeLocations}
          message={`${projectLocations.length} / ${locationCount} locations`}
        />
        <Box
          sx={{
            padding: "8px",
            height: `${windowHeight - 220}px`,
            overflow: "auto",
          }}
        >
          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Select
              label="Banners"
              value={filter?.bannerId ? filter.bannerId : "all"}
              items={projectBanners}
              onChange={(newValue) => handleFilterChange("bannerId", newValue)}
              includeNone={true}
              noneLabel="All Banners"
            />
            <Input
              label="location #"
              value={filter?.locationNumber || ""}
              fullwidth={true}
              onChange={(newValue) =>
                handleFilterChange("locationNumber", newValue || "all")
              }
            />
            <Select
              label="Territories"
              value={filter?.territoryId ? filter.territoryId : "all"}
              items={projectTerritories}
              onChange={(newValue) =>
                handleFilterChange("territoryId", newValue)
              }
              includeNone={true}
              noneLabel="All Territories"
            />
          </Box>
          {projectLocations.length > 0 ? (
            <Box>
              <Box
                sx={{
                  height: `${windowHeight - 310}px`,
                  overflow: "auto",
                }}
              >
                <Stack
                  spacing={1}
                  sx={{
                    paddingBottom: "10px",
                    paddingRight: "8px",
                    paddingTop: "8px",
                  }}
                >
                  {projectLocations.map((location) => (
                    <Box sx={{ display: "flex", paddingBottom: "10px" }}>
                      <IconButton
                        id="menuButton"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={(event) => handleMenuClick(event, location)}
                      >
                        <MenuIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        id="locationsMenu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        slotProps={{
                          list: {
                            "aria-labelledby": "basic-button",
                          },
                        }}
                      >
                        <MenuItem onClick={handleShowCalls}>Calls</MenuItem>
                        <MenuItem onClick={handleShowLocation}>
                          Location
                        </MenuItem>
                        <MenuItem onClick={handleShowListings}>
                          Listings
                        </MenuItem>
                      </Menu>
                      <Box sx={{ flex: 1 }}>
                        <LocationItem location={location} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  onClick={getNext}
                >
                  More...
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography>No locations assigned to project</Typography>
          )}
        </Box>
      </Box>

      {showCalls && selectedLocation ? (
        <CallsModal
          open={showCalls}
          onClose={handleCloseCalls}
          filter={{ projectId, locationId: selectedLocation }}
          onRefresh={() => {}}
        />
      ) : null}

      {showLocation && selectedLocation ? (
        <ProjectLocationModal
          open={showLocation}
          onClose={handleCloseLocation}
          projectId={projectId}
          locationId={selectedLocation}
          onRefresh={() => {}}
        />
      ) : null}

      {showListings && selectedLocation ? (
        <LocationListingsModal
          open={showListings}
          onClose={handleCloseListings}
          clientId={project?.clientId}
          locationId={selectedLocation}
        />
      ) : null}
    </>
  );
};

export default ProjectLocations;
